import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type { RelationshipScoreCreateSchema, RelationshipScoreUpdateSchema } from './schemas';
import type {
  RelationshipScoreQueryAllSchema,
  RelationshipScoreQuerySchema,
} from './schemas/relationship-score-query-schema';

export function useGetRelationshipScores(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['RELATIONSHIP_SCORES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<RelationshipScoreQueryAllSchema>({
        endpoint: '/api/v1/relationship-scores',
        params: `${apiParamStr}&include=clientsCount,icon`,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['RELATIONSHIP_SCORES'] });
};

export const useGetRelationshipScoresMutation: UseControlledMutation<RelationshipScoreQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['RELATIONSHIP_SCORES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<RelationshipScoreQueryAllSchema>({
            endpoint: '/api/v1/relationship-scores',
            params: { search, per_page, include: 'icon' },
          });

          return orderBy(res?.data ?? [], ['label']) || [];
        },
      });
    },
  });

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, RelationshipScoreCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/relationship-scores/',
        payload,
      }),
  });

export function useGetRelationshipScore(id: string) {
  return useSuspenseQuery({
    queryKey: ['RELATIONSHIP_SCORE', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: RelationshipScoreQuerySchema }>({
        endpoint: `/api/v1/relationship-scores/${id}`,
        params: { include: 'icon' },
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: RelationshipScoreQuerySchema }, ApiError, Partial<RelationshipScoreUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/relationship-scores/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['RELATIONSHIP_SCORE_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/relationship-scores/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
